import { FC, useMemo, useState } from "react";
import Text from "@src/lib/ui/Text";

import useRoles from "@src/features/roles/hooks/useRoles";
import { Button } from "@src/lib/ui/Button";
import ChevronDown from "@src/lib/icons/18px/chevron-down";
import { Checkbox } from "@src/lib/ui/Checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
type Props = {
  label?: string;
  placeholder?: string;
  roleIds: number[];
  setRoleIds: (x: number[]) => void;
  excludedRoleIds?: number[];
};

export const RoleMultiSelectField: FC<Props> = (props) => {
  const [open, setOpen] = useState(false);
  const { roles } = useRoles();
  const filteredRoles = useMemo(() => {
    return roles.filter((x) => !props.excludedRoleIds?.includes(x.id));
  }, [props.excludedRoleIds, roles]);
  const selectedRoleNames = useMemo(() => {
    return filteredRoles
      .filter((r) => props.roleIds.includes(r.id))
      .map((r) => r.name)
      .join(", ");
  }, [filteredRoles, props.roleIds]);
  return (
    <div
      className="flex flex-col gap-1 self-stretch"
      data-testid={"roles-field"}
    >
      <div className="flex justify-between self-stretch">
        <Text variant="fine-print-title" className="text-black">
          {props.label || "Roles"}
        </Text>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid="multi-select-field-input"
            className="mb-1 flex w-full justify-between"
          >
            <div className="min-w-0 truncate">
              {selectedRoleNames || props.placeholder || "Select Roles"}
            </div>
            <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
          disablePortal
          data-testid="roles-popover-content"
        >
          <Command>
            <CommandInput placeholder="Search roles..." className="h-9" />
            <CommandList>
              <CommandEmpty>No roles found.</CommandEmpty>
              <CommandGroup>
                {filteredRoles.map((role) => {
                  const isChecked = props.roleIds.includes(role.id);
                  return (
                    <CommandItem
                      key={role.id}
                      value={role.name}
                      data-testid="multi-select-option"
                      onSelect={() => {
                        if (isChecked) {
                          props.setRoleIds(
                            props.roleIds.filter((id) => id !== role.id),
                          );
                        } else {
                          props.setRoleIds([...props.roleIds, role.id]);
                        }
                        setOpen(true);
                      }}
                      className="gap-2"
                    >
                      <Checkbox checked={isChecked} />
                      <Text variant="compact-description">{role.name}</Text>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default RoleMultiSelectField;
